import * as React from "react";
import {
  Brain,
  Compass,
  FileText,
  Headphones,
  HelpCircle,
  Layers,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * ExperienceTypeBadge — opaque, reusable chip that labels an AI Experience by
 * type (Quest / Video / Note / Podcast / Expert / Studio / Collection /
 * Experience). The whole "type → label, icon, tone, gradient" lookup lives
 * here so card domain shells, list rows, detail headers, fallback cover
 * placeholders, and any other surface that surfaces an Experience can pull
 * from one source of truth.
 *
 * Three appearances:
 * - **`solid`** (default) — fully opaque tone background with white text.
 *   Designed for cover overlays where translucency competes with the photo
 *   underneath. The badge strip on `<ExperienceCard>` uses this.
 * - **`soft`** — the existing `<CardTag>`-style pale tinted chip. Use in
 *   in-content contexts (filter bars, breadcrumbs) where a louder solid badge
 *   would be visually heavy.
 * - **`overlay`** — glass-tinted middle ground for cover photos.
 *
 * Pass `tone` to override the auto-selected colour for the type — useful when
 * a studio wants its own type colour palette without subclassing the badge.
 *
 * Icon + tone choices match the Creator Studio + AI Studio (v3) consumer apps
 * so a card swapped from a local icon-map to this primitive looks identical.
 */

export type ExperienceType =
  | "quest"
  | "video"
  | "note"
  | "podcast"
  | "expert"
  | "studio"
  | "collection"
  | "experience"
  | string;

export type ExperienceTypeTone =
  | "emerald"
  | "sky"
  | "cyan"
  | "violet"
  | "amber"
  | "rose"
  | "slate"
  | "neutral";

interface ExperienceTypeMeta {
  label: string;
  /** A lucide mark, or a product-owned glyph with the same props contract
   * (the studio mark is the platform's rail icon, which lucide does not have). */
  Icon: LucideIcon | React.ComponentType<{ className?: string } & React.AriaAttributes>;
  tone: ExperienceTypeTone;
  /** Tailwind gradient classes for fallback covers, matching the badge tone. */
  gradient: string;
}

/* The Studios mark -- three vertical panels (narrow / wide / narrow), the same
 * glyph the consumer apps' rail uses for Studios (lucide has no matching mark).
 * Owner ruling 2026-07-24: an AI Studio chip wears THIS identity, never the
 * Sparkles stand-in. */
function StudiosGlyph({ className, ...aria }: { className?: string } & React.AriaAttributes) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
      {...aria}
    >
      <rect x="2" y="4" width="4" height="12" rx="1" />
      <rect x="7" y="4" width="6.5" height="12" rx="1" />
      <rect x="14.5" y="4" width="4" height="12" rx="1" />
    </svg>
  );
}

const TYPE_META: Record<string, ExperienceTypeMeta> = {
  quest: {
    label: "Quiz",
    Icon: Compass,
    tone: "violet",
    gradient: "from-violet-500/30 via-purple-400/20 to-fuchsia-300/15",
  },
  video: {
    label: "Video",
    Icon: Video,
    tone: "cyan",
    gradient: "from-cyan-500/30 via-sky-400/20 to-blue-300/15",
  },
  note: {
    label: "Note",
    Icon: FileText,
    tone: "sky",
    gradient: "from-sky-500/30 via-blue-400/20 to-indigo-300/15",
  },
  podcast: {
    label: "Podcast",
    Icon: Headphones,
    tone: "amber",
    gradient: "from-amber-500/30 via-orange-400/20 to-rose-300/15",
  },
  expert: {
    label: "Expert",
    Icon: Brain,
    tone: "emerald",
    gradient: "from-emerald-500/30 via-green-400/20 to-teal-300/15",
  },
  studio: {
    label: "AI Studio",
    Icon: StudiosGlyph,
    tone: "slate",
    gradient: "from-slate-500/30 via-slate-400/20 to-zinc-300/15",
  },
  collection: {
    label: "Collection",
    Icon: Layers,
    tone: "slate",
    gradient: "from-slate-500/25 via-slate-400/15 to-zinc-300/10",
  },
  experience: {
    label: "Experience",
    Icon: Sparkles,
    tone: "neutral",
    gradient: "from-muted via-muted/60 to-muted/30",
  },
};

const FALLBACK_META: ExperienceTypeMeta = {
  label: "Experience",
  Icon: HelpCircle,
  tone: "neutral",
  gradient: "from-muted via-muted/60 to-muted/30",
};

/**
 * Look up the meta for a given experience type. Useful for fallback covers
 * (which need the gradient + icon) and any surface that needs the canonical
 * label without rendering the badge itself. Falls back to a neutral shape for
 * unknown types so the consumer never has to handle missing keys.
 */
export const getExperienceTypeMeta = (type: ExperienceType): ExperienceTypeMeta =>
  TYPE_META[(type ?? "").toLowerCase()] ?? { ...FALLBACK_META, label: type || FALLBACK_META.label };

const experienceTypeBadgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap font-medium rounded-full border shadow-sm",
  {
    variants: {
      appearance: {
        solid: "",
        soft: "",
        overlay: "backdrop-blur-sm",
      },
      tone: {
        emerald: "",
        sky: "",
        cyan: "",
        violet: "",
        amber: "",
        rose: "",
        slate: "",
        neutral: "",
      },
      size: {
        sm: "px-2 py-0.5 text-[10px] [&_svg]:size-3",
        md: "px-2.5 py-1 text-xs [&_svg]:size-3.5",
        lg: "px-3 py-1.5 text-sm [&_svg]:size-4",
      },
    },
    compoundVariants: [
      // Solid — opaque tone bg, white-ish text. Reads on photo covers.
      {
        appearance: "solid",
        tone: "emerald",
        class: "bg-emerald-500 text-white border-emerald-600/30",
      },
      { appearance: "solid", tone: "sky", class: "bg-sky-500 text-white border-sky-600/30" },
      { appearance: "solid", tone: "cyan", class: "bg-cyan-500 text-white border-cyan-600/30" },
      {
        appearance: "solid",
        tone: "violet",
        class: "bg-violet-500 text-white border-violet-600/30",
      },
      { appearance: "solid", tone: "amber", class: "bg-amber-500 text-white border-amber-600/30" },
      { appearance: "solid", tone: "rose", class: "bg-rose-500 text-white border-rose-600/30" },
      { appearance: "solid", tone: "slate", class: "bg-slate-600 text-white border-slate-700/30" },
      {
        appearance: "solid",
        tone: "neutral",
        class: "bg-secondary text-secondary-foreground border-border",
      },
      // Soft — pale tinted bg, dark text. Pairs with in-content surfaces.
      {
        appearance: "soft",
        tone: "emerald",
        class:
          "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-400/30",
      },
      {
        appearance: "soft",
        tone: "sky",
        class:
          "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/20 dark:text-sky-200 dark:border-sky-400/30",
      },
      {
        appearance: "soft",
        tone: "cyan",
        class:
          "bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-500/20 dark:text-cyan-200 dark:border-cyan-400/30",
      },
      {
        appearance: "soft",
        tone: "violet",
        class:
          "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-500/20 dark:text-violet-200 dark:border-violet-400/30",
      },
      {
        appearance: "soft",
        tone: "amber",
        class:
          "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-400/30",
      },
      {
        appearance: "soft",
        tone: "rose",
        class:
          "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-400/30",
      },
      {
        appearance: "soft",
        tone: "slate",
        class:
          "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/20 dark:text-slate-200 dark:border-slate-400/30",
      },
      {
        appearance: "soft",
        tone: "neutral",
        class: "bg-muted text-muted-foreground border-border",
      },
      // Overlay — middle ground between fully-solid and the old translucent
      // card-colour treatment. The chip keeps a hint of tone via the icon +
      // text colour but the background is a glassy card-tinted surface so it
      // recedes against photographic covers. Use this on cover overlays
      // where the bright solid would feel garish.
      {
        appearance: "overlay",
        tone: "emerald",
        class: "bg-background/85 text-emerald-700 border-emerald-500/30 dark:text-emerald-300",
      },
      {
        appearance: "overlay",
        tone: "sky",
        class: "bg-background/85 text-sky-700 border-sky-500/30 dark:text-sky-300",
      },
      {
        appearance: "overlay",
        tone: "cyan",
        class: "bg-background/85 text-cyan-700 border-cyan-500/30 dark:text-cyan-300",
      },
      {
        appearance: "overlay",
        tone: "violet",
        class: "bg-background/85 text-violet-700 border-violet-500/30 dark:text-violet-300",
      },
      {
        appearance: "overlay",
        tone: "amber",
        class: "bg-background/85 text-amber-700 border-amber-500/30 dark:text-amber-300",
      },
      {
        appearance: "overlay",
        tone: "rose",
        class: "bg-background/85 text-rose-700 border-rose-500/30 dark:text-rose-300",
      },
      {
        appearance: "overlay",
        tone: "slate",
        class: "bg-background/85 text-slate-700 border-slate-500/30 dark:text-slate-300",
      },
      {
        appearance: "overlay",
        tone: "neutral",
        class: "bg-background/85 text-foreground border-border",
      },
    ],
    defaultVariants: { appearance: "solid", size: "md", tone: "neutral" },
  },
);

export interface ExperienceTypeBadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    Pick<VariantProps<typeof experienceTypeBadgeVariants>, "appearance" | "size"> {
  /** The experience type. Unknown values render with a neutral tone + the value as the label. */
  type: ExperienceType;
  /** Override the auto-selected tone. */
  tone?: ExperienceTypeTone;
  /** Skip the icon, render label only. */
  noIcon?: boolean;
  /** Skip the label, render icon only (square chip). */
  iconOnly?: boolean;
  /**
   * Surface-specific wording in place of the canonical type label.
   *
   * The type-to-label lookup here stays the single source of truth, and a consumer
   * that hand-rolls its own label is the anti-pattern this component exists to
   * prevent. But one surface legitimately renames a type for its audience: a share
   * card presents a video as "Video Overview" to a RECIPIENT, not "Video" to its
   * creator. This prop is that seam, so such a surface asks the badge for different
   * wording instead of rebuilding the chip around a local string (PQA#2620).
   *
   * Tone, icon and shape are unaffected: it overrides the words, never the identity.
   * Omit for the canonical label.
   */
  label?: string;
}

export const ExperienceTypeBadge = React.forwardRef<HTMLSpanElement, ExperienceTypeBadgeProps>(
  (
    {
      type,
      tone,
      appearance = "solid",
      size = "md",
      noIcon = false,
      iconOnly = false,
      label,
      className,
      ...rest
    },
    ref,
  ) => {
    const meta = getExperienceTypeMeta(type);
    const finalTone = tone ?? meta.tone;
    const Icon = meta.Icon;
    // A blank / whitespace override is a consumer bug, not a request for an empty
    // chip, so it falls back to the canonical label rather than rendering nothing.
    const shownLabel = label?.trim() || meta.label;
    return (
      <span
        ref={ref}
        className={cn(
          experienceTypeBadgeVariants({ appearance, size, tone: finalTone }),
          iconOnly && "p-1.5",
          className,
        )}
        {...rest}
      >
        {noIcon ? null : <Icon aria-hidden />}
        {iconOnly ? null : <span>{shownLabel}</span>}
      </span>
    );
  },
);
ExperienceTypeBadge.displayName = "ExperienceTypeBadge";
